import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  DeleteHistoryInput,
  DeleteHistoryMutation,
  DeleteHistoryMutationVariables,
  DeleteHistoryResponse,
  ExecutionSource,
  ExecutionStatus,
  OnValidateQueryRecordsSubscription,
  OnValidateQueryRecordsSubscriptionVariables,
  QueryStatus,
  QueryValidationRecord,
  QueryWithExecutionRecord,
  ValidationStatus,
} from '../../../../API';
import { QueryHistoryService } from '../../services/query-history.service';
import { ApiService } from '../../../shared/services/api.service';
import { onValidateQueryRecords } from '../../../../graphql/subscriptions';
import { Subscription } from 'rxjs';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DeleteHistory } from '../../../../graphql/mutations';
import { HistoryCategory, HistoryRow, SegmentHistoryRecord, WorkbenchHistoryRecord } from '../../models/history-model';
import { ActivatedRoute } from '@angular/router';
import { WorkbenchHistoryComponent } from './workbench-history/workbench-history.component';
import { SchedulerHistoryComponent } from './scheduler-history/scheduler-history.component';

interface NotificationNavigationParams {
  queryType?: string;
  queryId?: string;
  executionId?: string;
}

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrl: './history.component.scss',
})
export class HistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(WorkbenchHistoryComponent)
  workbenchHistoryComponent!: WorkbenchHistoryComponent;
  @ViewChild(SchedulerHistoryComponent)
  schedulerHistoryComponent!: SchedulerHistoryComponent;
  @ViewChild('deleteHistoryConfirmContent', { static: true })
  deleteHistoryConfirmContent!: TemplateRef<void>;

  readonly HistoryCategory = HistoryCategory;

  selectedCategory: HistoryCategory = HistoryCategory.Workbench;
  searchTerm = '';
  isLoading = false;

  workbenchRows: WorkbenchHistoryRecord[] = [];
  schedulerRows: WorkbenchHistoryRecord[] = [];
  segmentRows: SegmentHistoryRecord[] = [];
  graphqlValidationSubscription?: Subscription;
  selectedQueryRecord: QueryWithExecutionRecord | null = null;

  constructor(
    private queryHistoryService: QueryHistoryService,
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private nzMessageService: NzMessageService,
    private modalService: NzModalService,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit(): Promise<void> {
    this.listenRealTimeValidationChanges();
    await this.loadHistory();
  }

  ngAfterViewInit(): void {
    this.route.queryParams.subscribe((queryParams) => {
      const queryTypeParam = queryParams['queryType'];
      if (queryTypeParam !== undefined) {
        this.onCategoryChange(queryTypeParam);
        setTimeout(() => {
          this.openViewModalFromParent(queryParams);
        }, 500);
      }
    });
  }

  ngOnDestroy(): void {
    this.graphqlValidationSubscription?.unsubscribe();
  }

  clearSearch(): void {
    this.searchTerm = '';
  }

  onCategoryChange(category: HistoryCategory): void {
    this.selectedCategory = category;
    this.clearSearch();
  }

  private async loadHistory(): Promise<void> {
    this.isLoading = true;

    try {
      const segmentHistoryRequest = this.isMarketingUser
        ? Promise.resolve<SegmentHistoryRecord[]>([])
        : this.queryHistoryService.getSegmentHistory();
      const [workbenchRows, schedulerRows, segmentRows] = await Promise.all([
        this.queryHistoryService.getWorkbenchHistory(),
        this.queryHistoryService.getSchedulerHistory(),
        segmentHistoryRequest,
      ]);

      this.workbenchRows = workbenchRows;
      this.schedulerRows = schedulerRows;
      this.segmentRows = segmentRows;
    } catch (error) {
      console.error('Failed to load history data', error);
    } finally {
      this.isLoading = false;
    }
  }

  private listenRealTimeValidationChanges(): void {
    const orgId = this.userManagementService.getOrgId();

    if (!orgId) {
      return;
    }

    this.graphqlValidationSubscription = this.apiService
      .subscription<
        OnValidateQueryRecordsSubscription,
        OnValidateQueryRecordsSubscriptionVariables
      >(onValidateQueryRecords, { orgId })
      .subscribe({
        next: (event: { data?: OnValidateQueryRecordsSubscription }) => {
          const updatedItem = event.data?.onValidateQueryRecords;

          if (!updatedItem) {
            return;
          }

          this.applyRealtimeStatusUpdate(updatedItem);
        },
        error: (error: unknown) => {
          console.warn('Subscription error: ', error);
        },
      }) as Subscription;
  }

  private applyRealtimeStatusUpdate(updatedItem: QueryValidationRecord): void {
    const updatedStatus = updatedItem.status ?? this.getUpdatedStatus(updatedItem);

    this.workbenchRows = this.workbenchRows.map((row) =>
      this.isMatchingRecord(row.queryId, row.executionId, updatedItem.queryId, updatedItem.executionId)
        ? {
            ...row,
            status: updatedStatus ?? row.status,
            lastModifiedAt: updatedItem.lastModifiedAt || row.lastModifiedAt,
            lastModifiedBy: updatedItem.lastModifiedBy?.trim() || row.lastModifiedBy,
            athenaExecutionId: updatedItem.athenaExecutionId || row.athenaExecutionId,
            queryName: updatedItem.queryName?.trim() || row.queryName,
          }
        : row,
    );

    this.schedulerRows = this.schedulerRows.map((row) =>
      this.isMatchingRecord(row.queryId, row.executionId, updatedItem.queryId, updatedItem.executionId)
        ? {
            ...row,
            status: updatedStatus ?? row.status,
            lastModifiedAt: updatedItem.lastModifiedAt || row.lastModifiedAt,
            lastModifiedBy: updatedItem.lastModifiedBy?.trim() || row.lastModifiedBy,
            athenaExecutionId: updatedItem.athenaExecutionId || row.athenaExecutionId,
            queryName: updatedItem.queryName?.trim() || row.queryName,
          }
        : row,
    );

    this.segmentRows = this.segmentRows.map((row) =>
      this.isMatchingRecord(row.queryId, row.executionId, updatedItem.queryId, updatedItem.executionId)
        ? {
            ...row,
            status: updatedStatus ?? row.status,
            createdOn: updatedItem.lastModifiedAt || row.createdOn,
            createdBy: updatedItem.lastModifiedBy?.trim() || row.createdBy,
            segmentName: updatedItem.queryName?.trim() || row.segmentName,
            segmentedFans: this.toSegmentedFans(updatedItem.segmentedFanCount, row.segmentedFans),
          }
        : row,
    );
  }

  private toSegmentedFans(value: string | null | undefined, fallback: number): number {
    if (value == null) {
      return fallback;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private getUpdatedStatus(updatedItem: QueryValidationRecord): QueryStatus | null {
    if (updatedItem.executionStatus) {
      switch (updatedItem.executionStatus) {
        case ExecutionStatus.SUCCESS:
          return QueryStatus.EXECUTED;
        case ExecutionStatus.FAIL:
          return QueryStatus.FAILED;
        case ExecutionStatus.IN_PROGRESS:
          return QueryStatus.IN_PROGRESS;
      }
    }

    if (updatedItem.validationStatus) {
      switch (updatedItem.validationStatus) {
        case ValidationStatus.SUCCESS:
          return QueryStatus.VALIDATED;
        case ValidationStatus.FAIL:
          return QueryStatus.VALIDATION_FAILED;
        case ValidationStatus.IN_PROGRESS:
          return QueryStatus.IN_PROGRESS;
      }
    }

    return null;
  }

  private isMatchingRecord(
    rowQueryId: string,
    rowExecutionId: string,
    subscriptionQueryId?: string | null,
    subscriptionExecutionId?: string | null,
  ): boolean {
    return (
      !!subscriptionQueryId &&
      rowQueryId === subscriptionQueryId &&
      !!subscriptionExecutionId &&
      rowExecutionId === subscriptionExecutionId
    );
  }

  get searchPlaceholder(): string {
    switch (this.selectedCategory) {
      case HistoryCategory.Scheduler:
        return 'Search scheduler history';
      case HistoryCategory.FanSegment:
        return 'Search segment history';
      default:
        return 'Search workbench history';
    }
  }

  get workbenchCount(): number {
    return this.workbenchRows.length;
  }

  get schedulerCount(): number {
    return this.schedulerRows.length;
  }

  get fanSegmentCount(): number {
    return this.segmentRows.length;
  }

  get isMarketingUser(): boolean {
    return this.userManagementService.isMarketingUser();
  }

  async deleteHistory(row: HistoryRow): Promise<void> {
    this.modalService.confirm({
      nzTitle: 'Delete Query History?',
      nzContent: this.deleteHistoryConfirmContent,
      nzOkText: 'Delete Forever',
      nzOkDanger: true,
      nzOnOk: () => this.delete(row),
      nzCancelText: 'Cancel',
      nzClassName: 'delete-history-confirm-modal',
      nzClosable: true,
      nzIconType: 'exclamation-circle',
    });
  }

  private delete(row: HistoryRow): void {
    this.isLoading = true;
    const orgId = this.userManagementService.getOrgId();
    const deleteHitoryInput: DeleteHistoryInput = {
      orgId: orgId,
      athenaExecutionId: row?.athenaExecutionId ?? '',
      executionId: row?.executionId,
      executionSource: this.getExecutionSource(row?.querySource),
      queryId: row?.queryId,
    };

    this.apiService
      .query<DeleteHistoryMutation, DeleteHistoryMutationVariables>(DeleteHistory, {
        input: deleteHitoryInput,
      })
      .then(async (response) => {
        const deleteResponse = response.data?.DeleteHistory as DeleteHistoryResponse;
        if (deleteResponse.statusCode !== 200) {
          this.nzMessageService.error(`Failed to delete history: ${deleteResponse.message}`);
        } else {
          this.nzMessageService.success(`${this.getHistoryDisplayName(row)} is deleted successfully from history.`);
          await this.loadHistory();
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to delete history: ${error}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private getHistoryDisplayName(row: HistoryRow): string {
    if ('queryName' in row) {
      return row.queryName.trim() || 'Query';
    }

    return row.segmentName.trim() || 'Query';
  }

  getExecutionSource(sourceName?: string): ExecutionSource {
    switch (sourceName) {
      case 'Workbench':
        return ExecutionSource.WORKBENCH;
      case 'Scheduler':
        return ExecutionSource.SCHEDULER;
      default:
        return ExecutionSource.SEGMENT;
    }
  }

  async openViewModalFromParent(category: NotificationNavigationParams): Promise<void> {
    const queryTypeParam = category['queryType'];
    const queryId = category['queryId'];
    const executionId = category['executionId'];

    if (!queryId || !executionId) {
      console.error('Unable to load query details: queryId or executionId is missing.');
      return;
    }

    try {
      const queryRecord = await this.queryHistoryService.getQueryRecordById(queryId, executionId);

      if (!queryRecord) {
        console.error('Unable to load query details for the selected notification.');
        return;
      }

      this.selectedQueryRecord = queryRecord;

      const row: WorkbenchHistoryRecord = {
        orgId: this.userManagementService.getOrgId(),
        executionId,
        queryId,
        athenaExecutionId: queryRecord.athenaExecutionId,
        queryName: queryRecord.queryName?.trim() || '-',
        status: queryRecord.status ?? QueryStatus.DRAFT,
        querySource: queryTypeParam === 'scheduler' ? 'Scheduler' : 'Workbench',
        schedulerName: queryRecord.schedulerName?.trim() || '-',
        lastModifiedAt: queryRecord.lastModifiedAt || '',
        lastModifiedBy: queryRecord.lastModifiedBy?.trim() || '-',
      };
      if (queryTypeParam === 'workbench' && this.workbenchHistoryComponent !== undefined) {
        await this.workbenchHistoryComponent.openViewQueryModal(row);
      } else if (queryTypeParam === 'scheduler' && this.schedulerHistoryComponent !== undefined) {
        await this.schedulerHistoryComponent.openViewQueryModal(row);
      }
    } catch (error) {
      console.error('Failed to load query details', error);
    }
  }
}
